/**
 * React Query hooks for Scorecard Pages.
 *
 * Provides data fetching for My Scorecards, Latest Scorecards,
 * Scorecard Stats, and Browse by Season pages.
 */
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { mlbApi } from '../services/api';
import { STALE_TIMES, QUERY_RETRY_CONFIG } from '../types/statcast';
import type {
  UserScorecardProfileResponse,
  LatestScorecardsResponse,
  TopUploadersResponse,
  MostScoredGamesResponse,
  SeasonSummaryResponse,
  SeasonDetailResponse,
  FullStatListResponse,
} from '../services/api';
import type { ScorecardStatsResponse } from '../types/scorecardStats';

// Query key factory for consistent cache management
export const scorecardPagesKeys = {
  all: ['scorecard-pages'] as const,
  myProfile: (filters?: ProfileSliceFilters) =>
    [...scorecardPagesKeys.all, 'my-profile', filters ?? {}] as const,
  userProfile: (username: string, page?: number, filters?: ProfileSliceFilters) =>
    [...scorecardPagesKeys.all, 'user', username, { page, ...filters }] as const,
  userStats: (username: string, statType: string) =>
    [...scorecardPagesKeys.all, 'user', username, 'stats', statType] as const,
  latest: (limit?: number) => [...scorecardPagesKeys.all, 'latest', limit] as const,
  topUploaders: () => [...scorecardPagesKeys.all, 'top-uploaders'] as const,
  topUploadersSeason: () => [...scorecardPagesKeys.all, 'top-uploaders-season'] as const,
  mostScoredGames: () => [...scorecardPagesKeys.all, 'most-scored-games'] as const,
  seasonSummary: () => [...scorecardPagesKeys.all, 'season-summary'] as const,
  seasonDetail: (year: number, limit?: number, offset?: number) =>
    [...scorecardPagesKeys.all, 'season', year, { limit, offset }] as const,
  statsOverview: () => [...scorecardPagesKeys.all, 'stats-overview'] as const,
};

/**
 * Optional filters for the My Scorecards / User Profile slice.
 * Arrays are the caller-facing shape; the hook serializes them to CSV
 * when building query params, matching the Archive page contract.
 */
export interface ProfileSliceFilters {
  seasons?: number[];
  competitions?: string[];
  teams?: string[];
  gameTypes?: string[];
  playerIds?: number[];
  sort?: string | null;
  page?: number;
}

function joinCsv(values: ReadonlyArray<string | number> | undefined): string | null {
  if (!values || values.length === 0) return null;
  return values.join(',');
}

/**
 * Fetch current user's scorecard profile (My Scorecards page).
 * Requires authentication. Optional season/competition filters produce
 * a server-side slice of the user's data.
 */
export function useMyScorecardsProfile(filters: ProfileSliceFilters = {}) {
  const { getToken, isSignedIn } = useAuth();
  const page = filters.page ?? 1;

  return useQuery<UserScorecardProfileResponse>({
    queryKey: scorecardPagesKeys.myProfile({ ...filters, page }),
    queryFn: async () => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return mlbApi.getMyScorecardsProfile(token, {
        page,
        season: joinCsv(filters.seasons),
        competition: joinCsv(filters.competitions),
        team: joinCsv(filters.teams),
        game_type: joinCsv(filters.gameTypes),
        player: joinCsv(filters.playerIds),
        sort: filters.sort ?? null,
      });
    },
    enabled: !!isSignedIn,
    staleTime: STALE_TIMES.LIVE_DATA,
    placeholderData: keepPreviousData,
    ...QUERY_RETRY_CONFIG,
  });
}

/**
 * Fetch a user's public scorecard profile.
 * Works for both authenticated and anonymous users.
 * Supports pagination via page/perPage params plus optional slice filters.
 */
export function useUserScorecardProfile(
  username: string | undefined,
  page: number = 1,
  perPage: number = 12,
  filters: ProfileSliceFilters = {}
) {
  const { getToken } = useAuth();

  return useQuery<UserScorecardProfileResponse>({
    queryKey: scorecardPagesKeys.userProfile(username ?? '', page, filters),
    queryFn: async () => {
      if (!username) throw new Error('Username required');
      const token = await getToken();
      return mlbApi.getUserScorecardProfile(
        username,
        {
          page,
          per_page: perPage,
          season: joinCsv(filters.seasons),
          competition: joinCsv(filters.competitions),
          team: joinCsv(filters.teams),
          game_type: joinCsv(filters.gameTypes),
          player: joinCsv(filters.playerIds),
          sort: filters.sort ?? null,
        },
        token ?? undefined
      );
    },
    enabled: !!username,
    staleTime: STALE_TIMES.LIVE_DATA,
    placeholderData: keepPreviousData,
    retry: (failureCount, error) => {
      // Don't retry on private profile error
      if (error instanceof Error && error.message === 'profile_private') {
        return false;
      }
      return failureCount < 3;
    },
  });
}

/**
 * Fetch full stat list for a user (expanded view).
 */
export function useUserFullStatList(username: string | undefined, statType: string | undefined) {
  const { getToken } = useAuth();

  return useQuery<FullStatListResponse>({
    queryKey: scorecardPagesKeys.userStats(username ?? '', statType ?? ''),
    queryFn: async () => {
      if (!username || !statType) throw new Error('Username and stat type required');
      const token = await getToken();
      return mlbApi.getUserFullStatList(username, statType, token ?? undefined);
    },
    enabled: !!username && !!statType,
    staleTime: STALE_TIMES.LIVE_DATA,
    ...QUERY_RETRY_CONFIG,
  });
}

/**
 * Fetch latest public scorecards.
 * Passes auth token when available for is_liked/is_mine data.
 */
export function useLatestScorecards(limit: number = 20) {
  const { getToken } = useAuth();

  return useQuery<LatestScorecardsResponse>({
    queryKey: scorecardPagesKeys.latest(limit),
    queryFn: async () => {
      const token = await getToken();
      return mlbApi.getLatestScorecards(limit, token ?? undefined);
    },
    staleTime: STALE_TIMES.LIVE_DATA,
    ...QUERY_RETRY_CONFIG,
  });
}

/**
 * Fetch top uploaders all-time.
 */
export function useTopUploaders(limit: number = 10) {
  return useQuery<TopUploadersResponse>({
    queryKey: scorecardPagesKeys.topUploaders(),
    queryFn: () => mlbApi.getTopUploaders(limit),
    staleTime: STALE_TIMES.CAREER_STATS,
    ...QUERY_RETRY_CONFIG,
  });
}

/**
 * Fetch top uploaders for current season.
 */
export function useTopUploadersCurrentSeason(limit: number = 10) {
  return useQuery<TopUploadersResponse>({
    queryKey: scorecardPagesKeys.topUploadersSeason(),
    queryFn: () => mlbApi.getTopUploadersCurrentSeason(limit),
    staleTime: STALE_TIMES.CURRENT_SEASON,
    ...QUERY_RETRY_CONFIG,
  });
}

/**
 * Fetch most scored games.
 */
export function useMostScoredGames(limit: number = 20) {
  return useQuery<MostScoredGamesResponse>({
    queryKey: scorecardPagesKeys.mostScoredGames(),
    queryFn: () => mlbApi.getMostScoredGames(limit),
    staleTime: STALE_TIMES.CAREER_STATS,
    ...QUERY_RETRY_CONFIG,
  });
}

/**
 * Fetch season summary for decade grid.
 */
export function useSeasonSummary() {
  return useQuery<SeasonSummaryResponse>({
    queryKey: scorecardPagesKeys.seasonSummary(),
    queryFn: () => mlbApi.getSeasonSummary(),
    staleTime: STALE_TIMES.PLAYER_INFO,
    ...QUERY_RETRY_CONFIG,
  });
}

/**
 * Fetch scorecards for a specific season.
 */
export function useSeasonScorecards(
  year: number | undefined,
  limit: number = 100,
  offset: number = 0
) {
  return useQuery<SeasonDetailResponse>({
    queryKey: scorecardPagesKeys.seasonDetail(year ?? 0, limit, offset),
    queryFn: () => {
      if (!year) throw new Error('Year required');
      return mlbApi.getSeasonScorecards(year, { limit, offset });
    },
    enabled: !!year && year >= 1901 && year <= 2030,
    staleTime: STALE_TIMES.CURRENT_SEASON,
    ...QUERY_RETRY_CONFIG,
  });
}

/**
 * Fetch comprehensive scorecard statistics overview.
 * Includes hero metrics, distributions, leaderboards, and fun facts.
 */
export function useScorecardStatsOverview() {
  return useQuery<ScorecardStatsResponse>({
    queryKey: scorecardPagesKeys.statsOverview(),
    queryFn: () => mlbApi.getScorecardStatsOverview(),
    staleTime: STALE_TIMES.LIVE_DATA,
    ...QUERY_RETRY_CONFIG,
  });
}
